import React from 'react';

import { MAX_SKILL_NAME_CHARS } from '@aline/common';
import { IconButton } from '@mui/material';
import type { LucideIcon } from 'lucide-react';
import { ChevronLeft, Copy, Play, Sparkles, Trash2, Undo2 } from 'lucide-react';
import { AlineButton, AlineTooltip } from 'src/components';
import { InlineEditField } from 'src/components/generic/InlineEditInput';
import type { Apollo } from 'src/gql/generated';
import { makeStyles } from 'tss-react/mui';

import { SkillAccessMenu } from './skill-access-menu';
import type { EditableSkillScope } from './skill-access-menu';

const useStyles = makeStyles()(theme => ({
  actionButton: { color: theme.appColors.secondaryType, flexShrink: 0 },
  backButton: { color: theme.appColors.iconsType },
  titleDisplay: {
    color: theme.appColors.iconsType,
    fontSize: 22,
    fontWeight: 700,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  titleGroup: { alignItems: 'center', display: 'flex', flex: 1, gap: 8, minWidth: 0 },
  titleInput: { fontSize: 22, fontWeight: 700 },
  titlePlaceholder: { color: theme.appColors.secondaryType },
  titleRow: { alignItems: 'center', display: 'flex', gap: theme.spacing(1), minWidth: 0 },
  // Matches the 32px buttons: an odd row height centers them on a half pixel.
  titleWrap: { lineHeight: '32px', margin: 0, minWidth: 0 },
  toolbar: {
    alignItems: 'center',
    borderBottom: `1px solid ${theme.appColors.rule}`,
    columnGap: theme.spacing(0.5),
    display: 'grid',
    gridTemplateColumns: 'auto minmax(0, 1fr)',
    padding: theme.spacing(1, 2),
    rowGap: theme.spacing(0.5),
  },
  toolbarActions: { alignItems: 'center', display: 'flex', gap: theme.spacing(1) },
}));

const RUN_ICON = <Play size={14} />;

export const SkillBackButton = React.memo(function SkillBackButton({
  onBack,
}: {
  onBack: () => void;
}) {
  const { classes } = useStyles();
  return (
    <AlineTooltip arrow placement="top" title="Back to skills">
      <IconButton
        aria-label="Back to skills"
        className={classes.backButton}
        size="small"
        onClick={onBack}
      >
        <ChevronLeft size={20} />
      </IconButton>
    </AlineTooltip>
  );
});

/** The row above a skill's editor, with Back, the name, sharing and the skill's actions. */
export const SkillEditorToolbar = React.memo(function SkillEditorToolbar({
  canEdit,
  children,
  initiallyEditingName,
  name,
  nameEditable,
  onBack,
  onNameSave,
  onScopeChange,
  scope,
  scopeLocked,
  skillId,
  title,
}: {
  canEdit: boolean;
  /** The skill's actions, after the sharing control. */
  children: React.ReactNode;
  initiallyEditingName: boolean;
  name: string;
  nameEditable: boolean;
  onBack: () => void;
  onNameSave: (name: string) => void;
  onScopeChange: (scope: EditableSkillScope) => void;
  scope: Apollo.AgentSkillScope;
  scopeLocked: boolean;
  skillId: string;
  /** The skill's name as shown, with a fallback for an unnamed one. */
  title: string;
}) {
  const { classes } = useStyles();
  return (
    <header className={classes.toolbar}>
      <SkillBackButton onBack={onBack} />
      <div className={classes.titleRow}>
        <div className={classes.titleGroup}>
          <h1 aria-label={title} className={classes.titleWrap}>
            <InlineEditField
              key={skillId}
              classes={{
                display: classes.titleDisplay,
                input: classes.titleInput,
                placeholder: classes.titlePlaceholder,
              }}
              disabled={!nameEditable}
              editTooltip="Rename skill"
              iconSize={16}
              initiallyEditing={initiallyEditingName}
              maxLength={MAX_SKILL_NAME_CHARS}
              placeholder="Type a skill title..."
              textPlaceholder="Untitled skill"
              value={name}
              onSave={onNameSave}
            />
          </h1>
        </div>
        <div className={classes.toolbarActions}>
          <SkillAccessMenu
            busy={scopeLocked}
            canEdit={canEdit}
            scope={scope}
            onChange={onScopeChange}
          />
          {children}
        </div>
      </div>
    </header>
  );
});

const ActionIconButton = React.memo(function ActionIconButton({
  disabled,
  icon: Icon,
  label,
  onClick,
  tooltip = label,
}: {
  disabled: boolean;
  icon: LucideIcon;
  label: string;
  onClick: () => void;
  tooltip?: string;
}) {
  const { classes } = useStyles();
  return (
    <AlineTooltip arrow placement="top" title={tooltip}>
      <span>
        <IconButton
          aria-label={label}
          className={classes.actionButton}
          disabled={disabled}
          size="small"
          onClick={onClick}
        >
          <Icon size={16} />
        </IconButton>
      </span>
    </AlineTooltip>
  );
});

/**
 * Edit with AI, Duplicate, and Delete, then for an editor Discard and Publish, and last
 * Test or Run, for the toolbar's `children`. A viewer still sees Edit with AI, disabled,
 * with a tooltip that says why. Test tries the draft; Run uses the published skill.
 */
export const SkillEditorActions = React.memo(function SkillEditorActions({
  canEdit,
  deleteDisabled,
  discardDisabled,
  duplicateDisabled,
  editWithAiDisabled,
  onDelete,
  onDiscard,
  onDuplicate,
  onEditWithAi,
  onPublish,
  onRun,
  publishBlocker,
  publishDisabled,
  publishLabel,
  publishing,
  runBlocker,
  runDisabled,
  runLabel,
  running,
}: {
  canEdit: boolean;
  deleteDisabled: boolean;
  discardDisabled: boolean;
  duplicateDisabled: boolean;
  editWithAiDisabled: boolean;
  onDelete: () => void;
  /** Shown when the published skill has changes to discard. */
  onDiscard: (() => void) | null;
  onDuplicate: () => void;
  onEditWithAi: () => void;
  /** Shown to the author. */
  onPublish: (() => void) | null;
  onRun: () => void;
  /** Why Publish is unavailable, shown on hover; empty when it can be pressed. */
  publishBlocker: string;
  publishDisabled: boolean;
  publishing: boolean;
  publishLabel: string;
  /** Why Test or Run is unavailable, shown on hover; empty when it can be pressed. */
  runBlocker: string;
  runDisabled: boolean;
  runLabel: 'Run' | 'Test';
  running: boolean;
}) {
  return (
    <>
      <ActionIconButton
        disabled={!canEdit || editWithAiDisabled}
        icon={Sparkles}
        label="Edit this skill with AI"
        tooltip={
          canEdit
            ? 'Edit this skill with AI'
            : 'Only the author or an org admin can edit this skill'
        }
        onClick={onEditWithAi}
      />
      <ActionIconButton
        disabled={duplicateDisabled}
        icon={Copy}
        label="Duplicate skill"
        onClick={onDuplicate}
      />
      <ActionIconButton
        disabled={deleteDisabled}
        icon={Trash2}
        label="Delete skill"
        tooltip={canEdit ? 'Delete skill' : 'Only the author or an org admin can delete this skill'}
        onClick={onDelete}
      />
      {onDiscard && (
        <ActionIconButton
          disabled={discardDisabled}
          icon={Undo2}
          label="Discard unpublished changes"
          onClick={onDiscard}
        />
      )}
      {onPublish && (
        <AlineTooltip
          arrow
          describeChild
          placement="top"
          title={publishDisabled ? publishBlocker : ''}
        >
          <span>
            <AlineButton
              color="primary"
              disabled={publishDisabled}
              loading={publishing}
              size="small"
              variant="outlined"
              onClick={onPublish}
            >
              {publishLabel}
            </AlineButton>
          </span>
        </AlineTooltip>
      )}
      <AlineTooltip arrow describeChild placement="top" title={runDisabled ? runBlocker : ''}>
        <span>
          <AlineButton
            color="primary"
            disabled={runDisabled}
            loading={running}
            size="small"
            startIcon={RUN_ICON}
            variant="contained"
            onClick={onRun}
          >
            {runLabel}
          </AlineButton>
        </span>
      </AlineTooltip>
    </>
  );
});
